<template>
  <section class="admin-content" id="single-ticket">
    <Navbar />
    <main class="admin-main">
      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-start justify-content-between text-white" style="padding: 32px 0 40px;">
            <div class="d-flex align-items-start" style="gap: 16px;">
              <div class="header-icon-wrap mt-1">
                <i class="fe fe-message-circle" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-1 font-weight-bold">{{ complaint.data.title || 'Ticket Details' }}</h4>
                <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                  <span class="status-badge" :class="statusClass(complaint.data.status)">
                    {{ formatStatus(complaint.data.status) }}
                  </span>
                  <small style="opacity: 0.75;">
                    <i class="fe fe-calendar mr-1"></i>{{ formatDate(complaint.data.created_at) }}
                  </small>
                  <small v-if="complaint.data.type" style="opacity: 0.75;">
                    <i class="fe fe-tag mr-1"></i>{{ complaint.data.type }}
                  </small>
                  <span v-if="complaint.data.priority" class="priority-chip"
                    :class="`priority-chip--${complaint.data.priority.toLowerCase()}`">
                    <i :class="priorityIcon(complaint.data.priority)" class="mr-1"></i>{{ complaint.data.priority }}
                  </span>
                  <small v-if="complaint.data.source" style="opacity: 0.75;">
                    <i :class="sourceIcon(complaint.data.source)" class="mr-1"></i>{{ complaint.data.source }}
                  </small>
                </div>
              </div>
            </div>
            <router-link to="/my-complaints" class="btn btn-outline-light btn-sm mt-1">
              <i class="fe fe-arrow-left mr-1"></i> All Tickets
            </router-link>
          </div>
        </div>
      </div>

      <section style="margin-top: -24px;">
        <div class="container pb-5">
          <div class="row">
            <!-- ── Left Column ── -->
            <div class="col-lg-7 mb-4">
              <!-- Ticket Info Card -->
              <div class="card shadow-sm detail-card mb-4">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-info" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Ticket Information</h6>
                </div>
                <div class="card-body p-4">
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-align-left mr-1"></i>Description</span>
                    <span class="info-value">{{ complaint.data.description || '—' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-user mr-1"></i>Enrollee</span>
                    <span class="info-value">{{ enrolleeName }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-home mr-1"></i>Provider</span>
                    <span class="info-value">{{ providerName(complaint.data.provider_id) }}</span>
                  </div>
                  <div class="info-row" v-if="complaint.data.priority">
                    <span class="info-label"><i class="fe fe-flag mr-1"></i>Priority</span>
                    <span class="info-value">
                      <span :class="`priority-chip priority-chip--${complaint.data.priority.toLowerCase()}`">
                        {{ complaint.data.priority }}
                      </span>
                    </span>
                  </div>
                  <div class="info-row" v-if="complaint.data.source">
                    <span class="info-label"><i class="fe fe-inbox mr-1"></i>Source</span>
                    <span class="info-value d-flex align-items-center">
                      <i :class="`${sourceIcon(complaint.data.source)} mr-2`" style="font-size: 1rem; color: #64748b;"></i>
                      {{ complaint.data.source }}
                    </span>
                  </div>
                  <div class="info-row" style="border-bottom: none;">
                    <span class="info-label"><i class="fe fe-users mr-1"></i>Assigned Staff</span>
                    <span class="info-value">
                      <template v-if="complaint.assigned_staff && complaint.assigned_staff.length">
                        <span
                          v-for="staff in complaint.assigned_staff"
                          :key="staff.id"
                          class="staff-chip"
                        >{{ staff.full_name }} </span>
                      </template>
                      <span v-else class="text-muted">Not assigned</span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Status Update (staff/admin only) -->
              <div class="card shadow-sm detail-card mb-4" v-if="user && user.type !== 'client'">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-sliders" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Update Status</h6>
                </div>
                <div class="card-body p-4">
                  <div class="d-flex align-items-center" style="gap: 12px;">
                    <select class="form-control" style="max-width: 200px;" v-model="assignment.status">
                      <option value="unanswered">Unanswered</option>
                      <option value="open">Open</option>
                      <option value="resolved">Resolved</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button class="btn btn-submit" @click="saveStatus">
                      <i class="fe fe-check mr-1"></i> Save Status
                    </button>
                  </div>
                </div>
              </div>

              <!-- Comments Chat Feed -->
              <div class="card shadow-sm detail-card">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-message-square" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Conversation</h6>
                  <span class="badge badge-light ml-auto" style="border-radius: 20px;">
                    {{ (complaint.comments || []).length }}
                  </span>
                </div>
                <div class="card-body p-4">
                  <div v-if="complaint.comments && complaint.comments.length" class="chat-feed">
                    <div
                      v-for="comment in complaint.comments"
                      :key="comment.id"
                      class="chat-msg"
                    >
                      <div
                        class="chat-avatar"
                        :style="avatarColor(comment.user)"
                      >{{ initials(comment.user) }}</div>
                      <div class="chat-bubble-wrap">
                        <div class="chat-meta">
                          <span class="chat-name">
                            {{ fullName(comment.user) }}
                          </span>
                          <span
                            v-if="comment.user && comment.user.job_title"
                            class="chat-role"
                          >{{ comment.user.job_title }}</span>
                          <span class="chat-time">{{ formatDate(comment.created_at) }}</span>
                        </div>
                        <div class="chat-bubble">{{ comment.body }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-4 text-muted">
                    <i class="fe fe-message-circle" style="font-size: 2rem; opacity: 0.25;"></i>
                    <p class="mt-2 mb-0 small">No messages yet. Add the first comment.</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Right Column ── -->
            <div class="col-lg-5">
              <!-- Assignment Card -->
              <div class="card shadow-sm detail-card mb-4" v-if="user && user.type !== 'client'">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-user-check" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Assignment</h6>
                </div>
                <div class="card-body p-4">
                  <div class="form-group">
                    <label class="small font-weight-bold text-secondary">
                      <i class="fe fe-home mr-1"></i>Provider
                    </label>
                    <select class="form-control" v-model="assignment.provider_id">
                      <option :value="null">Select provider…</option>
                      <option v-for="provider in providers" :key="provider.id" :value="provider.id">
                        {{ providerLabel(provider) }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group mb-4">
                    <label class="small font-weight-bold text-secondary">
                      <i class="fe fe-users mr-1"></i>Assigned Staff
                    </label>
                    <v-select
                      v-model="assignment.staff_ids"
                      :options="availableStaff"
                      :get-option-label="s => `${s.full_name} ${s.lastname} (${s.job_title})`"
                      :reduce="s => s.id"
                      multiple
                      placeholder="Search and select staff…"
                    />
                  </div>
                  <button class="btn btn-submit btn-block" @click="saveAssignment">
                    <i class="fe fe-save mr-1"></i> Save Assignment
                  </button>
                </div>
              </div>

              <!-- Add Comment Card -->
              <div class="card shadow-sm detail-card">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-edit-2" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Add Comment</h6>
                </div>
                <div class="card-body p-4">
                  <div v-if="!commentsLocked">
                    <textarea
                      class="form-control"
                      rows="5"
                      v-model="body"
                      placeholder="Add an update, resolution note, or question…"
                    ></textarea>
                    <button
                      class="btn btn-submit btn-block mt-3"
                      @click="addComment"
                      :disabled="!body"
                    >
                      <i class="fe fe-send mr-1"></i> Submit Comment
                    </button>
                  </div>
                  <div v-else class="text-center py-3 text-muted small">
                    <i class="fe fe-lock" style="font-size: 1.5rem; opacity: 0.3;"></i>
                    <p class="mt-2 mb-0">Comments are locked once a ticket is marked resolved or completed.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
  // let agency = JSON.parse(localStorage.getItem("agency"));

const AVATAR_COLORS = [
  ["#dbeafe", "#1d4ed8"],
  ["#d1fae5", "#065f46"],
  ["#fce7f3", "#9d174d"],
  ["#fef3c7", "#92400e"],
  ["#ede9fe", "#5b21b6"],
  ["#ffedd5", "#9a3412"],
];

export default {
  components: { Navbar, Loading },
  data() {
    return {
      user: JSON.parse(localStorage.getItem("user")),
      agency: JSON.parse(localStorage.getItem("agency")),
      isLoading: false,
      fullPage: true,
      body: "",
      providers: [],
      availableStaff: [],
      complaint: {
        data: {},
        comments: [],
        assigned_staff: [],
        enrollee: {},
      },
      assignment: {
        provider_id: null,
        staff_ids: [],
        status: "open",
      },
    };
  },
  computed: {
    enrolleeName() {
      if (!this.complaint.enrollee) return "Unknown enrollee";
      return `${this.complaint.enrollee.firstname || ""} ${this.complaint.enrollee.lastname || ""}`.trim();
    },
    commentsLocked() {
      return ["resolved", "closed", "completed"].includes(this.complaint.data.status);
    },
  },
  methods: {
    initials(user) {
      if (!user) return "?";
      return ((user.firstname || "").charAt(0) + (user.lastname || "").charAt(0)).toUpperCase();
    },
    fullName(user) {
      if (!user) return "Unknown";
      return `${user.firstname || ""} ${user.lastname || ""}`.trim();
    },
    avatarColor(user) {
      if (!user || !user.id) return `background:${AVATAR_COLORS[0][0]};color:${AVATAR_COLORS[0][1]}`;
      const pair = AVATAR_COLORS[user.id % AVATAR_COLORS.length];
      return `background:${pair[0]};color:${pair[1]}`;
    },
    providerLabel(provider) {
      return provider.agency_name || provider.provider_name || provider.full_name || provider.firstname || `Provider #${provider.id}`;
    },
    providerName(providerId) {
      if (!providerId) return "Not assigned";
      const p = this.providers.find((item) => item.id === providerId);
      return p ? this.providerLabel(p) : `Provider #${providerId}`;
    },
    priorityIcon(priority) {
      const icons = {
        Urgent: "fe fe-alert-triangle",
        Important: "fe fe-alert-circle",
        Routine: "fe fe-circle",
      };
      return icons[priority] || "fe fe-flag";
    },
    sourceIcon(source) {
      const icons = {
        "Call Centre": "fe fe-phone-call",
        "Phone Call": "fe fe-phone-call",
        Website: "fe fe-globe",
        WhatsApp: "mdi mdi-whatsapp",
        Facebook: "mdi mdi-facebook",
        LinkedIn: "mdi mdi-linkedin",
        Instagram: "mdi mdi-instagram",
        X: "mdi mdi-twitter",
        Office: "fe fe-briefcase",
      };
      return icons[source] || "fe fe-inbox";
    },
    statusClass(status) {
      if (["resolved", "closed"].includes(status)) return "status-resolved";
      if (status === "completed") return "status-completed";
      if (status === "open") return "status-open";
      return "status-unanswered";
    },
    formatStatus(status) {
      if (!status) return "Unknown";
      if (status === "closed") return "Resolved";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString();
    },
    normalizeCollection(payload) {
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      return [];
    },
    getComplaint() {
      this.axios
        .get(`/api/v1/auth/complaint/${this.$route.params.id}`)
        .then((response) => {
          this.complaint = response.data;
          this.assignment.provider_id = this.complaint.data.provider_id;
          this.assignment.staff_ids = (this.complaint.assigned_staff || []).map((s) => s.id);
          this.assignment.status = this.complaint.data.status || "open";
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchProviders() {
      const agencyId = (this.user && this.user.institutional_id) || (this.agency && this.agency.id);
      if (!agencyId) return;
      this.axios
        .get(`/api/v1/auth/providerAgency/${agencyId}`)
        .then((response) => {
          this.providers = this.normalizeCollection(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getAssignableStaff() {
      const agencyId = this.agency && this.agency.id;
      if (!agencyId) return;
      this.axios
        .get(`/api/v1/auth/getEmployee/${agencyId}`)
        .then((response) => {
          this.availableStaff = this.normalizeCollection(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    saveAssignment() {
      this.isLoading = true;
      this.axios
        .put(`/api/v1/auth/complaints/${this.complaint.data.id}`, {
          title: this.complaint.data.title,
          description: this.complaint.data.description,
          type: this.complaint.data.type,
          provider_id: this.assignment.provider_id,
          staff_ids: this.assignment.staff_ids,
        })
        .then(() => {
          this.getComplaint();
          this.isLoading = false;
          this.$toasted.info("Assignment updated", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    saveStatus() {
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/update-complaint/status`, {
          id: this.complaint.data.id,
          status: this.assignment.status,
        })
        .then(() => {
          this.getComplaint();
          this.isLoading = false;
          this.$toasted.info("Status updated", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    addComment() {
      if (!this.body) return;
      this.axios
        .post(`/api/v1/auth/addComment`, {
          user_id: this.user.id,
          complaint_id: this.$route.params.id,
          body: this.body,
        })
        .then(() => {
          this.body = "";
          this.getComplaint();
          this.$toasted.info("Comment submitted", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
  created() {
    this.getComplaint();
    if (this.user && this.user.type !== "client") {
      this.fetchProviders();
      this.getAssignableStaff();
    }
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.header-icon-wrap {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.section-icon {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

/* Info rows */
.info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.info-label {
  min-width: 150px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #94a3b8;
  padding-top: 2px;
  flex-shrink: 0;
}

.info-value {
  color: #1e293b;
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Staff chips */
.staff-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 6px;
  margin-bottom: 4px;
}

/* Priority badge */
.priority-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.priority-chip--urgent {
  background: #fee2e2;
  color: #991b1b;
}

.priority-chip--important {
  background: #fef3c7;
  color: #92400e;
}

.priority-chip--routine {
  background: #ede0d4;
  color: #6b4423;
}

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-unanswered {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.status-open {
  background: #fef3c7;
  color: #92400e;
}

.status-resolved {
  background: #d1fae5;
  color: #065f46;
}

.status-completed {
  background: #dbeafe;
  color: #1d4ed8;
}

/* Chat feed */
.chat-feed {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.chat-msg {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.chat-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.chat-bubble-wrap {
  flex: 1;
  min-width: 0;
}

.chat-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.chat-name {
  font-weight: 700;
  font-size: 0.88rem;
  color: #1e293b;
}

.chat-role {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
}

.chat-time {
  font-size: 0.72rem;
  color: #94a3b8;
  margin-left: auto;
}

.chat-bubble {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0 12px 12px 12px;
  padding: 12px 16px;
  font-size: 0.88rem;
  color: #334155;
  line-height: 1.6;
  word-break: break-word;
}

/* Submit button */
.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  padding: 10px 20px;
}

.btn-submit:hover {
  opacity: 0.9;
  color: #fff;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.15);
}
</style>
